import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { Container, Tab, Nav, Button } from "react-bootstrap";
import PageLoading from "components/PageLoading";
import { FaArrowLeft } from "react-icons/fa";
import { InfoCircle } from "react-bootstrap-icons";
import NotificationToast from "../../../../../components/NotificationToast/NotificationToast";
import TopicAnalysis from "./TopicAnalysis";
import CadastroAnalysis from "./CadastroAnalysis";
import AnalysisActions from "../../ComponentsAnalysis/AnalysisActions/AnalysisActions";
import IndiceCriterios from "../../../../../components/IndiceCriterios/IndiceCriterios";

import { getQuestionarioRespostaById } from "../../../../../services/respostaService";
import {
  getHistoricoAnalise,
  salvarAnalise,
  concluirAnalise,
  solicitarAdequacao,
} from "../../../../../services/analiseService";
import { getSindicatoById } from "../../../../../services/sindicatoService";
import { getDocumentosByRespostaId } from "../../../../../services/documentoService";
import { getSalarioMinimo } from "../../../../../services/salarioMinimoService";
import { scrollContentAreaToElement } from "utils/scrollContentArea";

import "./AnalysisTab.css";
import type {
  HistoricoAdequacaoItem,
  TabDocumentoItem,
  AnaliseValorItem,
  DadosGeraisAnalise,
} from "../../../../../types";
import SolicitacaoAdequacaoModal from "../../../Adequacao/ComponentsAdequacao/SolicitacaoAdequacaoBox";
import AdequacaoModalConfirmacao from "../../../Adequacao/ComponentsAdequacao/AdequacaoModalConfirmacao";
import { PATHS } from "../../../../../routes/paths";

const AnalysisTab = () => {
  const { idResposta, idSindicato } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [analiseConcluida, setAnaliseConcluida] = useState(false);

  const [activeTab, setActiveTab] = useState("cadastro");
  const [tabsData, setTabsData] = useState<{
    obrigatorios: TabDocumentoItem[];
    bonus: TabDocumentoItem[];
    extra: TabDocumentoItem[];
  }>({
    obrigatorios: [],
    bonus: [],
    extra: [],
  });

  const [sindicato, setSindicato] = useState<unknown>(null);
  const [resposta, setResposta] = useState<unknown>(null);
  const [dadosGerais, setDadosGerais] = useState<DadosGeraisAnalise>({});
  const [uploadedFiles] = useState<Record<string, unknown>>({});
  const [isLoading, setIsLoading] = useState(true);

  const [analiseValor, setAnaliseValor] = useState<Record<string, AnaliseValorItem>>({});
  const [titulosValor, setTituloValor] = useState({});
  const [errosAnalise, setErrosAnalise] = useState({});
  const [toastData, setToastData] = useState<{
    show: boolean;
    message: React.ReactNode;
    type: string;
  }>({
    show: false,
    message: "",
    type: "",
  });

  const [NaoConformes, setNaoConformes] = useState<HistoricoAdequacaoItem[]>([]);
  const [podeAdequar, setPodeAdequar] = useState(false);
  const [showSolicitacaoModal, setShowSolicitacaoModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selecionadosParaEnvio, setSelecionadosParaEnvio] = useState<
    HistoricoAdequacaoItem[]
  >([]);
  const [historico, setHistorico] = useState<HistoricoAdequacaoItem[]>([]);
  const [quantidadeAssociados, setQuantidadeAssociados] = useState(0);
  const [isIndiceOpen, setIsIndiceOpen] = useState(false);
  const delay = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

  const [openByTab, setOpenByTab] = useState({
    "doc-obrigatorios": {},
    "doc-bonus": {},
    "doc-bonus-extra": {},
  });

  useEffect(() => {
    window.setQuantidadeAssociadosGlobal = (valor) => {
      setQuantidadeAssociados(valor);
    };
    return () => {
      window.setQuantidadeAssociadosGlobal = null;
    };
  }, []);

  useEffect(() => {
    if (dadosGerais?.quantidadeAssociados !== undefined) {
      const quantidade = dadosGerais.quantidadeAssociados || 0;
      setQuantidadeAssociados(quantidade);
    }
  }, [dadosGerais]);

  useEffect(() => {
    const state = (location.state || {}) as {
      situacao?: string | null;
      conclusaoAnalise?: string | null;
    };
    const situacaoNormalizada = String(state.situacao ?? "")
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");
    const concluidaViaState =
      Boolean(String(state.conclusaoAnalise ?? "").trim()) ||
      situacaoNormalizada.includes("analise concluida") ||
      situacaoNormalizada.includes("concluida") ||
      situacaoNormalizada.includes("concluido");
    if (concluidaViaState) setAnaliseConcluida(true);
  }, [location.state]);

  const filtrarNaoConformesSomenteSeUltimo = useCallback(
    (historicoLocal: HistoricoAdequacaoItem[] = []): HistoricoAdequacaoItem[] => {
      const ultimoPorCampo = new Map<string, HistoricoAdequacaoItem>();

      for (const item of historicoLocal) {
        if (!item || item.idCampoJson == null) continue;

        const atualData = new Date(item.dataSolicitacao || 0).getTime();
        const ultimo = ultimoPorCampo.get(item.idCampoJson);
        const ultimoData = new Date(ultimo?.dataSolicitacao || 0).getTime();

        if (!ultimo || atualData >= ultimoData) {
          ultimoPorCampo.set(item.idCampoJson, item);
        }
      }

      return Array.from(ultimoPorCampo.values())
        .filter((item) => item.conforme === false)
        .sort(
          (a, b) =>
            new Date(b.dataSolicitacao || 0).getTime() -
            new Date(a.dataSolicitacao || 0).getTime(),
        );
    },
    [],
  );

  const preencherCamposComHistorico = useCallback(
    async (data: {
      historicoAnaliseAdequacaoCampoQuestionarioJson?: HistoricoAdequacaoItem[];
    }) => {
      return new Promise<void>((resolve) => {
        const historicoLocal =
          data.historicoAnaliseAdequacaoCampoQuestionarioJson || [];

        const todosCamposEsperados = [
          ...tabsData.obrigatorios.map((t) => t.idCampoJson),
          ...tabsData.bonus.map((t) => t.idCampoJson),
          ...tabsData.extra.map((t) => t.idCampoJson),
        ];

        const naoConformes = filtrarNaoConformesSomenteSeUltimo(historicoLocal);
        setNaoConformes(naoConformes);

        const historicoMap: Record<string, HistoricoAdequacaoItem> = {};
        historicoLocal.forEach((item) => {
          if (item.idCampoJson) historicoMap[item.idCampoJson] = item;
        });

        const valores: Record<string, AnaliseValorItem> = {};

        todosCamposEsperados.forEach((idCampoJson) => {
          const item = historicoMap[idCampoJson];

          valores[idCampoJson] = {
            status: item
              ? item.conforme === true
                ? "CONFORME"
                : item.conforme === false
                  ? "NAO_CONFORME"
                  : null
              : null,
            observacao: item ? item.obsAnalista || "" : "",
            historico: {
              status: item
                ? item.conforme === true
                  ? "CONFORME"
                  : item.conforme === false
                    ? "NAO_CONFORME"
                    : null
                : null,
              observacao: item ? item.obsAnalista || "" : "",
              solicitadoEm: item?.dataSolicitacao || "",
              obsAnalista: item?.obsAnalista || "",
              adequadoEm: item?.dataAdequacao || "",
              adequacaoRealizada: item?.adequacaoRealizada || null,
              obsSindicato: item?.obsSindicato || "",
            },
          };
        });

        setAnaliseValor(valores);
        resolve();
      });
    },
    [tabsData, filtrarNaoConformesSomenteSeUltimo],
  );

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      await delay(300);
      const respostaResp = await getQuestionarioRespostaById(idResposta);
      setResposta(respostaResp.data);
      const statusNome = String(
        respostaResp?.data?.situacao?.nome ??
          respostaResp?.data?.status?.nome ??
          respostaResp?.data?.situacaoNome ??
          "",
      )
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");
      const concluidaPorPayload =
        Boolean(String(respostaResp?.data?.dataConclusaoAnalise ?? "").trim()) ||
        Boolean(String(respostaResp?.data?.analiseConcluidaEm ?? "").trim()) ||
        statusNome.includes("analise concluida") ||
        statusNome.includes("concluida") ||
        statusNome.includes("concluido");
      setAnaliseConcluida((prev) => prev || concluidaPorPayload);
      await delay(300);
      const sindicatoResp = await getSindicatoById(idSindicato);
      setSindicato(sindicatoResp.data);

      const anoReferencia =
        respostaResp?.data?.anoReferencia || new Date().getFullYear();
      const anoAnterior = anoReferencia - 1;

      const { valor: valorSalarioMinimo } = await getSalarioMinimo(anoAnterior);
      const salarioMinimoAnoAnterior = Number(valorSalarioMinimo) || 0;

      const evolucaoAssociados = respostaResp.data?.evolucaoAssociados || [];
      const evolucaoReceitaOperacional =
        respostaResp.data?.evolucaoReceitaOperacional || [];

      const quantidadeAssociadosRef =
        evolucaoAssociados.find((e) => e.anoReferencia === anoReferencia)
          ?.quantidade ?? 0;

      const dadosGeraisLocal = {
        id: respostaResp.data?.id,
        percentualConcluido: respostaResp.data?.percentualConcluido,
        percentEvolucaoReceita: respostaResp.data?.percentEvolucaoReceita,
        responsavelPreenchimento: respostaResp.data?.responsavelPreenchimento,
        evolucaoAssociados,
        evolucaoReceitaOperacional,
        salarioMinimoAnoAnterior,
        quantidadeAssociados: quantidadeAssociadosRef,
        anoReferencia,
      };

      setDadosGerais(dadosGeraisLocal);

      const rawJson = respostaResp.data?.json;
      const json = typeof rawJson === "string" ? JSON.parse(rawJson) : rawJson;
      const obrigatorios = json?.docsObrigatorios?.[0]?.tema || [];
      const bonus = json?.docsBonus?.[0]?.tema || [];
      const extra = json?.docsBonusExtra?.[0]?.tema || [];

      const documentos = await getDocumentosByRespostaId(idResposta);

      const normalizarTitulo = (titulo) => {
        let tituloNormalizado = (titulo || "")
          .toLowerCase()
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .replace(/[^\w\s-]/g, "")
          .replace(/^\d+\s*[-.]*\s*/, "")
          .trim()
          .replace(/\s+/g, "-");

        if (tituloNormalizado === "dre---demonstracoes-do-resultado") {
          tituloNormalizado = "dre-demonstracoes-do-resultado";
        }

        if (
          tituloNormalizado ===
          "defesa-de-interesse-implementada-no-executivo-ou-legislativo"
        ) {
          tituloNormalizado =
            "defesa-de-interesse-implementada-no-executivo-e-legislativo";
        }

        return tituloNormalizado;
      };

      const mapTemas = (temas, offset = 0) =>
        (temas || []).map((tema, idx) => {
          const tituloNormalizado = normalizarTitulo(tema.titulo);
          const arquivosDoTopico = (documentos || []).filter((doc) => {
            const nomeDocNormalizado = normalizarTitulo(doc.nomeArquivo);
            return nomeDocNormalizado.includes(tituloNormalizado);
          });

          return {
            id: offset + idx + 1,
            idCampoJson: tituloNormalizado,
            title: tema.titulo,
            criterios: tema.criterio || [],
            analise: tema.analise || [],
            link: tema.link || null,
            idResposta: idResposta,
            arquivos: arquivosDoTopico,
          };
        });

      const obrigatoriosMapeados = mapTemas(obrigatorios);
      const bonusMapeados = mapTemas(bonus, obrigatorios.length);
      const extraMapeados = mapTemas(extra, obrigatorios.length + bonus.length);

      const mapTitulos = {};
      [...obrigatoriosMapeados, ...bonusMapeados, ...extraMapeados].forEach(
        (item) => {
          mapTitulos[item.idCampoJson] = item.title;
        },
      );
      setTituloValor(mapTitulos);

      setTabsData({
        obrigatorios: obrigatoriosMapeados,
        bonus: bonusMapeados,
        extra: extraMapeados,
      });

      setOpenByTab((prev) => ({
        ...prev,
        "doc-obrigatorios": { ...prev["doc-obrigatorios"] },
        "doc-bonus": { ...prev["doc-bonus"] },
        "doc-bonus-extra": { ...prev["doc-bonus-extra"] },
      }));
    } catch (err) {
      console.error("❌ Erro ao carregar dados da análise:", err);
    } finally {
      setIsLoading(false);
    }
  }, [idResposta, idSindicato]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const existeNaoConforme = Object.values(analiseValor).some(
      (item) => item?.status === "NAO_CONFORME",
    );
    setPodeAdequar(existeNaoConforme);
  }, [analiseValor]);

  useEffect(() => {
    const carregarRespostaAnalise = async () => {
      if (
        !tabsData.obrigatorios.length &&
        !tabsData.bonus.length &&
        !tabsData.extra.length
      ) {
        return;
      }

      try {
        const response = await getHistoricoAnalise(idResposta ?? "");
        setAnaliseValor(
          (response as Record<string, AnaliseValorItem>) ?? {},
        );
        setHistorico(
          (response.historicoAnaliseAdequacaoCampoQuestionarioJson ??
            []) as HistoricoAdequacaoItem[],
        );
        preencherCamposComHistorico(response);
      } catch (e) { }
    };

    carregarRespostaAnalise();
  }, [tabsData, idResposta, preencherCamposComHistorico]);

  const handleAnalysisChange = (idCampo, novosValores) => {
    const chave = idCampo || "campoIndefinido";
    setAnaliseValor((prev) => ({
      ...prev,
      [chave]: novosValores,
    }));
  };

  const getRegistroParaSalvar = (
    idCampoJson: string,
    historicoLocal: HistoricoAdequacaoItem[],
    novoStatus: string | null,
    ignorarAdequacaoFinalizada = false,
  ): HistoricoAdequacaoItem | null => {
    const registrosDoCampo = historicoLocal
      .filter((item) => item.idCampoJson === idCampoJson)
      .sort((a, b) => (b.id ?? 0) - (a.id ?? 0));

    const registroMaisRecente = registrosDoCampo[0];

    if (
      !ignorarAdequacaoFinalizada &&
      registroMaisRecente &&
      (registroMaisRecente.adequacaoRealizada === true ||
        registroMaisRecente.adequacaoRealizada === 1) &&
      (registroMaisRecente.conforme === false ||
        registroMaisRecente.conforme === 0) &&
      novoStatus === "NAO_CONFORME"
    ) {
      return null;
    }
    return registroMaisRecente;
  };

  const mapAnaliseValorToPayload = (
    analiseValorLocal: Record<string, AnaliseValorItem>,
    idQuestionarioResposta: string,
    historicoLocal: HistoricoAdequacaoItem[] = [],
    idsSelecionados: string[] | null = null,
    forcarCriacaoNovaAdequacao = false,
    dadosComId: { idCampoJson: string; id?: number }[] = [],
    ignorarAdequacaoFinalizada = false,
  ) => {
    const analiseValorCompletado: Record<string, AnaliseValorItem> = {
      ...analiseValorLocal,
    };

    historicoLocal.forEach((item) => {
      const id = item.idCampoJson;
      if (id && item.conforme === false && !analiseValorCompletado[id]) {
        analiseValorCompletado[id] = {
          status: "NAO_CONFORME",
          observacao: item.obsAnalista || "",
          historico: {
            solicitadoEm: item.dataSolicitacao,
            dataAnalise: item.dataAnalise,
            adequacaoRealizada: item.adequacaoRealizada,
          },
        };
      }
    });

    return Object.entries(analiseValorCompletado)
      .filter(
        ([idCampoJson]) =>
          !["anoAtual", "anoAtualMenosUm"].includes(idCampoJson) &&
          (!idsSelecionados || idsSelecionados.includes(idCampoJson)),
      )
      .map(([idCampoJson, value]) => {
        const val = value as AnaliseValorItem;
        const registroPassado = dadosComId.find(
          (item) => item.idCampoJson === idCampoJson,
        );
        const registro =
          registroPassado ||
          getRegistroParaSalvar(
            idCampoJson,
            historicoLocal,
            val.status ?? null,
            ignorarAdequacaoFinalizada,
          );

        const deveZerarId = forcarCriacaoNovaAdequacao
          ? !registroPassado
          : !registro;
        const reg = registro as HistoricoAdequacaoItem | undefined;

        return {
          id: deveZerarId ? 0 : reg?.id,
          obsAnalista: val.observacao ?? "",
          conforme:
            val.status === "CONFORME"
              ? true
              : val.status === "NAO_CONFORME"
                ? false
                : null,
          idCampoJson,
          dataSolicitacao: val?.historico?.solicitadoEm || null,
          dataAnalise: val?.historico?.dataAnalise || null,
          adequacaoRealizada: val?.historico?.adequacaoRealizada || null,
          idQuestionarioResposta,
        };
      });
  };

  const handleSolicitarAdequacao = async () => {
    if (analiseConcluida) {
      setToastData({
        show: true,
        message: "Não é possível solicitar adequação: a análise já está concluída.",
        type: "error",
      });
      return;
    }
    const isValid = validarNaoConformes();
    if (!isValid) return;

    try {
      setIsLoading(true);

      const payload = {
        quantidadeAssociados:
          parseInt(analiseValor?.anoAtual?.resposta ?? "", 10) ||
          quantidadeAssociados ||
          0,
        apto: verificarSeApto(),
        analiseCamposQuestionarioResposta: mapAnaliseValorToPayload(
          analiseValor,
          String(dadosGerais?.id ?? "") as string,
          historico,
          null,
          false,
          [],
          true,
        ),
      };

      const response = await salvarAnalise(String(dadosGerais?.id ?? "") as string, payload);
      setHistorico(
        (response.historicoAnaliseAdequacaoCampoQuestionarioJson ??
          []) as HistoricoAdequacaoItem[],
      );
      setQuantidadeAssociados(quantidadeAssociados);

      await fetchData();

      const historicoAtualizado = await getHistoricoAnalise(idResposta ?? "");
      await preencherCamposComHistorico(historicoAtualizado);

      setToastData({
        show: true,
        message: "Solicitação de adequação salva com sucesso!",
        type: "success",
      });
    } catch (error) {
      console.error("❌ Erro ao solicitar adequação:", error);
      setToastData({
        show: true,
        message: "Erro ao solicitar adequação.",
        type: "error",
      });
    } finally {
      setIsLoading(false);
      setShowSolicitacaoModal(true);
    }
  };

  const handleSubmitAdequacao = async (
    dadosSelecionados: HistoricoAdequacaoItem[],
  ) => {
    if (analiseConcluida) {
      return {
        show: true,
        message: "Não é possível solicitar adequação: a análise já está concluída.",
        type: "error",
      };
    }
    try {
      setIsLoading(true);

      const historicoSimulado = [...historico];
      const analiseValorComObsModal = { ...analiseValor };

      const dadosFiltrados = dadosSelecionados.filter(
        (item) =>
          item.idCampoJson &&
          analiseValor?.[item.idCampoJson]?.status === "NAO_CONFORME",
      );

      dadosFiltrados.forEach((item) => {
        if (!item.idCampoJson) return;
        const chave = item.idCampoJson;
        const observacaoAtualizada = String(item.observacao ?? "").trim();
        analiseValorComObsModal[chave] = {
          ...(analiseValorComObsModal[chave] || { status: "NAO_CONFORME" }),
          status: "NAO_CONFORME",
          observacao: observacaoAtualizada,
        };
      });

      const dadosComId = dadosFiltrados
        .filter((item): item is HistoricoAdequacaoItem & { idCampoJson: string } =>
          Boolean(item.idCampoJson),
        )
        .map((item) => {
          const idCampoJson = item.idCampoJson;
          const registroExistente = getRegistroParaSalvar(
            idCampoJson,
            historicoSimulado,
            "NAO_CONFORME",
          );
          return {
            idCampoJson,
            id: registroExistente ? registroExistente.id : 0,
          };
        });

      const idsSelecionados = dadosComId.map((item) => item.idCampoJson);

      const payload = {
        quantidadeAssociados:
          parseInt(analiseValor?.anoAtual?.resposta ?? "", 10) ||
          quantidadeAssociados ||
          0,
        apto: verificarSeApto(),
        analiseCamposQuestionarioResposta: mapAnaliseValorToPayload(
          analiseValorComObsModal,
          String(idResposta ?? "") as string,
          historicoSimulado,
          idsSelecionados,
          true,
          dadosComId,
        ),
      };

      const response = await solicitarAdequacao(String(idResposta ?? "") as string, payload);
      setHistorico(
        (response.historicoAnaliseAdequacaoCampoQuestionarioJson ??
          []) as HistoricoAdequacaoItem[],
      );

      preencherCamposComHistorico({
        historicoAnaliseAdequacaoCampoQuestionarioJson:
          response.historicoAnaliseAdequacaoCampoQuestionarioJson,
      });

      await fetchData();
      setQuantidadeAssociados(quantidadeAssociados);

      setToastData({
        show: true,
        message: "Solicitação de adequação salva com sucesso!",
        type: "success",
      });

      return {
        show: true,
        message: "Solicitação de adequação salva com sucesso!",
        type: "success",
      };
    } catch (error) {
      setToastData({
        show: true,
        message: "Erro ao enviar adequação.",
        type: "error",
      });
      console.error("Erro ao enviar adequação", error);
      return {
        show: true,
        message: "Erro ao salvar Solicitação de Adequação.",
        type: "error",
      };
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (analiseConcluida) {
      setToastData({
        show: true,
        message: "Não é possível salvar: a análise já está concluída.",
        type: "error",
      });
      return;
    }
    try {
      setIsLoading(true);
      const quantidadeAssociadosAtual =
        parseInt(analiseValor?.anoAtual?.resposta ?? "", 10) || quantidadeAssociados || 0;

      const payload = {
        quantidadeAssociados: quantidadeAssociadosAtual,
        apto: verificarSeApto(),
        analiseCamposQuestionarioResposta: mapAnaliseValorToPayload(
          analiseValor,
          String(dadosGerais?.id ?? "") as string,
          historico,
          null,
          false,
          [],
          true,
        ),
      };

      const response = await salvarAnalise(String(dadosGerais?.id ?? "") as string, payload);
      await fetchData();
      setHistorico(
        (response.historicoAnaliseAdequacaoCampoQuestionarioJson ??
          []) as HistoricoAdequacaoItem[],
      );
      setQuantidadeAssociados(quantidadeAssociados);

      preencherCamposComHistorico({
        historicoAnaliseAdequacaoCampoQuestionarioJson:
          response.historicoAnaliseAdequacaoCampoQuestionarioJson,
      });

      setToastData({
        show: true,
        message: "Análise salva com sucesso.",
        type: "success",
      });
    } catch (error) {
      console.error("Erro ao salvar análise", error);
      setToastData({
        show: true,
        message: "Erro ao salvar análise.",
        type: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleConcluir = async () => {
    if (analiseConcluida) {
      setToastData({
        show: true,
        message: "Não é possível finalizar: a análise já está concluída.",
        type: "error",
      });
      return;
    }
    const isValid = validarAnalise();
    if (!isValid) return;

    try {
      setIsLoading(true);

      const payload = {
        quantidadeAssociados:
          parseInt(analiseValor?.anoAtual?.resposta ?? "", 10) ||
          quantidadeAssociados ||
          0,
        apto: verificarSeApto(),
        analiseCamposQuestionarioResposta: mapAnaliseValorToPayload(
          analiseValor,
          String(idResposta ?? "") as string,
          historico,
        ),
      };

      const response = await concluirAnalise(String(idResposta ?? "") as string, payload);
      await fetchData();
      setQuantidadeAssociados(quantidadeAssociados);
      setHistorico(
        (response.historicoAnaliseAdequacaoCampoQuestionarioJson ??
          []) as HistoricoAdequacaoItem[],
      );

      setErrosAnalise({});

      setToastData({
        show: true,
        message: "Análise concluída com sucesso.",
        type: "success",
      });
    } catch (e) {

      setToastData({
        show: true,
        message: "Erro ao concluir a análise.",
        type: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const validarAnalise = () => {
    const erros: Record<string, boolean> = {};
    const camposComErro: string[] = [];

    const todosTopicos: TabDocumentoItem[] = [
      // ...tabsData.obrigatorios,
      // ...tabsData.bonus,
      // ...tabsData.extra,
    ];

    todosTopicos.forEach((topico) => {
      const analise = analiseValor[topico.idCampoJson];
      const titulo = titulosValor[topico.idCampoJson] || topico.idCampoJson;

      const statusValido =
        analise?.status === "CONFORME" || analise?.status === "NAO_CONFORME";

      if (!statusValido) {
        erros[topico.idCampoJson] = true;
        camposComErro.push(`"${titulo}" sem status selecionado`);
      }

      if (
        analise?.status === "NAO_CONFORME" &&
        (!analise.observacao || analise.observacao.trim() === "")
      ) {
        erros[topico.idCampoJson] = true;
        camposComErro.push(`"${titulo}" está como NÃO CONFORME sem observação`);
      }
    });

    setErrosAnalise(erros);

    const isValid = Object.keys(erros).length === 0;

    if (!isValid) {
      setToastData({
        show: true,
        message: (
          <>
            <strong>Preencha corretamente:</strong>
            <ul>
              {camposComErro.map((item, idx) => (
                <li key={idx}>{item}</li>
              ))}
            </ul>
          </>
        ),
        type: "error",
      });
    }

    return isValid;
  };

  const validarNaoConformes = () => {
    const erros: Record<string, boolean> = {};
    const camposComErro: string[] = [];

    const todosTopicos: TabDocumentoItem[] = [
      ...tabsData.obrigatorios,
      // ...tabsData.bonus,
      // ...tabsData.extra,
    ];

    const naoConformes = todosTopicos.filter(
      (topico) => analiseValor[topico.idCampoJson]?.status === "NAO_CONFORME",
    );

    naoConformes.forEach((topico) => {
      const analise = analiseValor[topico.idCampoJson];
      const titulo = titulosValor[topico.idCampoJson] || topico.idCampoJson;

      if (!analise?.observacao || analise.observacao.trim() === "") {
        erros[topico.idCampoJson] = true;
        camposComErro.push(`"${titulo}" está como NÃO CONFORME sem observação`);
      }
    });

    setErrosAnalise(erros);

    const isValid = Object.keys(erros).length === 0;

    if (!isValid) {
      setToastData({
        show: true,
        message: (
          <>
            <strong>Preencha corretamente:</strong>
            <ul>
              {camposComErro.map((item, idx) => (
                <li key={idx}>{item}</li>
              ))}
            </ul>
          </>
        ),
        type: "error",
      });
    }

    return isValid;
  };

  const verificarSeApto = () => {
    return tabsData.obrigatorios.every((topico) => {
      const analise = analiseValor[topico.idCampoJson];
      return analise?.status === "CONFORME";
    });
  };

  const limparNumeroInicial = (texto) => {
    const s = String(texto || "").trim();
    return s.replace(/^\d+\s*([.)-]|-\s|–\s|—\s)?\s*/, "").trim();
  };

  const indiceItems = [
    {
      title: "DOCUMENTOS OBRIGATÓRIOS",
      links: (tabsData.obrigatorios || []).map((t) => ({
        tabKey: "doc-obrigatorios",
        id: `doc-obrigatorios-${t.idCampoJson}`,
        label: limparNumeroInicial(t.title),
      })),
    },
    {
      title: "DOCUMENTOS BÔNUS",
      links: (tabsData.bonus || []).map((t) => ({
        tabKey: "doc-bonus",
        id: `doc-bonus-${t.idCampoJson}`,
        label: limparNumeroInicial(t.title),
      })),
    },
    {
      title: "DOCUMENTOS BÔNUS EXTRA",
      links: (tabsData.extra || []).map((t) => ({
        tabKey: "doc-bonus-extra",
        id: `doc-bonus-extra-${t.idCampoJson}`,
        label: limparNumeroInicial(t.title),
      })),
    },
  ];

  const handleIndiceNavigate = async (link) => {
    if (!link?.id) return;

    const goScroll = () => {
      setTimeout(() => {
        const el = document.getElementById(link.id);
        if (el) {
          scrollContentAreaToElement(el, { behavior: "smooth" });
        }
      }, 80);
    };

    try {
      if (link.tabKey && link.tabKey !== activeTab) {
        setActiveTab(link.tabKey);
        goScroll();
        return;
      }
      goScroll();
    } catch (e) {
      console.error("Erro ao navegar pelo índice", e);
    }
  };

  const getCurrentTabTopics = () => {
    if (activeTab === "doc-obrigatorios") return tabsData.obrigatorios || [];
    if (activeTab === "doc-bonus") return tabsData.bonus || [];
    if (activeTab === "doc-bonus-extra") return tabsData.extra || [];
    return [];
  };

  const canToggleAll =
    activeTab !== "cadastro" && getCurrentTabTopics().length > 0;

  const isAllOpenOnTab = () => {
    if (!canToggleAll) return false;

    const topics = getCurrentTabTopics();
    const map = openByTab?.[activeTab] || {};
    return topics.every((t) => map[t.idCampoJson] === true);
  };

  const toggleAllOnTab = () => {
    if (!canToggleAll) return;

    const topics = getCurrentTabTopics();
    const nextValue = !isAllOpenOnTab();

    setOpenByTab((prev) => {
      const prevTab = prev?.[activeTab] || {};
      const nextTab = { ...prevTab };

      topics.forEach((t) => {
        nextTab[t.idCampoJson] = nextValue;
      });

      return { ...prev, [activeTab]: nextTab };
    });
  };

  const toggleOneTopic = (tabKey, idCampoJson) => {
    setOpenByTab((prev) => {
      const prevTab = prev?.[tabKey] || {};
      return {
        ...prev,
        [tabKey]: {
          ...prevTab,
          [idCampoJson]: !prevTab[idCampoJson],
        },
      };
    });
  };

  const orderedTabs = useMemo(
    () => ["cadastro", "doc-obrigatorios", "doc-bonus", "doc-bonus-extra"],
    [],
  );

  const currentTabIndex = orderedTabs.indexOf(activeTab);

  const goToPrevTab = () => {
    if (currentTabIndex <= 0) return;
    const prevKey = orderedTabs[currentTabIndex - 1];
    if (prevKey) setActiveTab(prevKey);
  };

  const goToNextTab = () => {
    if (currentTabIndex === -1) return;
    if (currentTabIndex >= orderedTabs.length - 1) return;
    const nextKey = orderedTabs[currentTabIndex + 1];
    if (nextKey) setActiveTab(nextKey);
  };

  const hasPrevTab = currentTabIndex > 0;
  const hasNextTab =
    currentTabIndex >= 0 && currentTabIndex < orderedTabs.length - 1;

  if (isLoading) return <PageLoading />;

  return (
    <Container className="pt-3" style={{ position: "relative" }}>
      <div
        className="merit-voltar-seta"
        style={{
          position: "absolute",
          top: "20px",
          right: "30px",
          zIndex: 10,
        }}
        onClick={() => navigate(PATHS.DOC_ANALYSIS)}
        title="Voltar para a lista"
      >
        <FaArrowLeft />
      </div>

      <NotificationToast
        show={toastData.show}
        onClose={() => setToastData((prev) => ({ ...prev, show: false }))}
        message={toastData.message}
        type={toastData.type}
      />

      <IndiceCriterios
        open={isIndiceOpen}
        onClose={() => setIsIndiceOpen(false)}
        items={indiceItems}
        onNavigate={handleIndiceNavigate}
      />

      <button
        type="button"
        onClick={() => setIsIndiceOpen(true)}
        className="analysis-index-btn analysis-index-btn--floating"
        aria-label="Abrir índice de critérios"
        title="Índice de critérios"
      >
        <InfoCircle className="analysis-index-btn-icon" aria-hidden size={22} />
      </button>

      <h1 className="analysis-title">Análise da Documentação</h1>

      <Tab.Container
        activeKey={activeTab}
        onSelect={(k) => k != null && setActiveTab(k)}
      >
        <div className="analysis-tabs-row" data-merit-ic-below>
          <Nav variant="tabs" className="mb-3 analysis-tabs">
            <Nav.Item>
              <Nav.Link eventKey="cadastro" className="analysis-tab">
                CADASTRO
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="doc-obrigatorios" className="analysis-tab">
                DOC. OBRIGATÓRIOS
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="doc-bonus" className="analysis-tab">
                DOC. BÔNUS
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="doc-bonus-extra" className="analysis-tab">
                DOC. BÔNUS EXTRA
              </Nav.Link>
            </Nav.Item>
          </Nav>
        </div>

        {canToggleAll && (
          <div className="analysis-actions-row">
            <button
              type="button"
              className="analysis-toggle-all"
              onClick={toggleAllOnTab}
              aria-label={isAllOpenOnTab() ? "Recolher tudo" : "Expandir tudo"}
              title={
                isAllOpenOnTab()
                  ? "Recolher todos os tópicos"
                  : "Expandir todos os tópicos"
              }
            >
              {isAllOpenOnTab() ? "Recolher tudo" : "Expandir tudo"}
            </button>
          </div>
        )}

        <Tab.Content>
          <Tab.Pane eventKey="cadastro">
            <CadastroAnalysis
              idResposta={idResposta ?? ""}
              idSindicato={idSindicato ?? ""}
              resposta={resposta as { json: string | Record<string, unknown> }}
              sindicato={sindicato as Record<string, unknown>}
            />

            <div className="analysis-bottom-nav">
              <Button
                type="button"
                variant="secondary"
                onClick={goToPrevTab}
                disabled={!hasPrevTab}
              >
                ANTERIOR
              </Button>
              <Button
                type="button"
                variant="primary"
                className="analysis-bottom-nav__next"
                onClick={goToNextTab}
                disabled={!hasNextTab}
              >
                PRÓXIMO
              </Button>
            </div>
          </Tab.Pane>

          <Tab.Pane eventKey="doc-obrigatorios">
            <h1 className="analysis-section-title">DOCUMENTOS OBRIGATÓRIOS</h1>
            {tabsData.obrigatorios.map((topic) => (
              <div
                key={topic.id}
                id={`doc-obrigatorios-${topic.idCampoJson}`}
                className="analysis-anchor"
              >
                <TopicAnalysis
                  resposta={resposta}
                  data={topic}
                  uploadedFiles={uploadedFiles}
                  analysisValues={analiseValor}
                  onAnalysisChange={handleAnalysisChange}
                  associadosDadosAPI={dadosGerais.evolucaoAssociadosReceita}
                  salarioMinimoAnoAnterior={
                    dadosGerais.salarioMinimoAnoAnterior
                  }
                  errosAnalise={errosAnalise}
                  evolucaoReceitaOperacional={
                    dadosGerais.evolucaoReceitaOperacional
                  }
                  apto={verificarSeApto()}
                  isOpen={
                    !!(openByTab["doc-obrigatorios"] || {})[topic.idCampoJson]
                  }
                  onToggle={() =>
                    toggleOneTopic("doc-obrigatorios", topic.idCampoJson)
                  }
                  analiseBloqueada={analiseConcluida}
                />
              </div>
            ))}

            <div className="d-flex justify-content-end">
              <AnalysisActions
                onSalvar={handleSave}
                onFinalizar={handleConcluir}
                onSolicitarAdequacao={handleSolicitarAdequacao}
                showSolicitar={podeAdequar}
                showEligibilityCheck={true}
                apto={verificarSeApto()}
                disabled={analiseConcluida}
              />
            </div>

            <div className="analysis-bottom-nav">
              <Button
                type="button"
                variant="secondary"
                onClick={goToPrevTab}
                disabled={!hasPrevTab}
              >
                ANTERIOR
              </Button>
              <Button
                type="button"
                variant="primary"
                className="analysis-bottom-nav__next"
                onClick={goToNextTab}
                disabled={!hasNextTab}
              >
                PRÓXIMO
              </Button>
            </div>
          </Tab.Pane>

          <Tab.Pane eventKey="doc-bonus">
            <h1 className="analysis-section-title">DOCUMENTOS BÔNUS</h1>
            {tabsData.bonus.map((topic) => (
              <div
                key={topic.id}
                id={`doc-bonus-${topic.idCampoJson}`}
                className="analysis-anchor"
              >
                <TopicAnalysis
                  data={topic}
                  resposta={resposta}
                  uploadedFiles={uploadedFiles}
                  analysisValues={analiseValor}
                  onAnalysisChange={handleAnalysisChange}
                  associadosDadosAPI={dadosGerais.evolucaoAssociadosReceita}
                  salarioMinimoAnoAnterior={
                    dadosGerais.salarioMinimoAnoAnterior
                  }
                  errosAnalise={errosAnalise}
                  evolucaoReceitaOperacional={
                    dadosGerais.evolucaoReceitaOperacional
                  }
                  evolucaoAssociados={dadosGerais.evolucaoAssociados}
                  apto={verificarSeApto()}
                  isOpen={!!(openByTab["doc-bonus"] || {})[topic.idCampoJson]}
                  onToggle={() =>
                    toggleOneTopic("doc-bonus", topic.idCampoJson)
                  }
                  analiseBloqueada={analiseConcluida}
                />
              </div>
            ))}

            <div className="d-flex justify-content-end">
              <AnalysisActions
                onSalvar={handleSave}
                onFinalizar={handleConcluir}
                onSolicitarAdequacao={handleSolicitarAdequacao}
                showSolicitar={podeAdequar}
                showEligibilityCheck={false}
                apto={verificarSeApto()}
                disabled={analiseConcluida}
              />
            </div>

            <div className="analysis-bottom-nav">
              <Button
                type="button"
                variant="secondary"
                onClick={goToPrevTab}
                disabled={!hasPrevTab}
              >
                ANTERIOR
              </Button>
              <Button
                type="button"
                variant="primary"
                className="analysis-bottom-nav__next"
                onClick={goToNextTab}
                disabled={!hasNextTab}
              >
                PRÓXIMO
              </Button>
            </div>
          </Tab.Pane>

          <Tab.Pane eventKey="doc-bonus-extra">
            <h1 className="analysis-section-title">DOCUMENTOS BÔNUS EXTRA</h1>
            {tabsData.extra.map((topic) => (
              <div
                key={topic.id}
                id={`doc-bonus-extra-${topic.idCampoJson}`}
                className="analysis-anchor"
              >
                <TopicAnalysis
                  data={topic}
                  resposta={resposta}
                  uploadedFiles={uploadedFiles}
                  analysisValues={analiseValor}
                  onAnalysisChange={handleAnalysisChange}
                  associadosDadosAPI={dadosGerais.evolucaoAssociadosReceita}
                  salarioMinimoAnoAnterior={
                    dadosGerais.salarioMinimoAnoAnterior
                  }
                  errosAnalise={errosAnalise}
                  evolucaoReceitaOperacional={
                    dadosGerais.evolucaoReceitaOperacional
                  }
                  apto={verificarSeApto()}
                  isOpen={
                    !!(openByTab["doc-bonus-extra"] || {})[topic.idCampoJson]
                  }
                  onToggle={() =>
                    toggleOneTopic("doc-bonus-extra", topic.idCampoJson)
                  }
                  analiseBloqueada={analiseConcluida}
                />
              </div>
            ))}

            <div className="d-flex justify-content-end">
              <AnalysisActions
                onSalvar={handleSave}
                onFinalizar={handleConcluir}
                onSolicitarAdequacao={handleSolicitarAdequacao}
                showSolicitar={podeAdequar}
                showEligibilityCheck={false}
                apto={verificarSeApto()}
                disabled={analiseConcluida}
              />
            </div>

            <div className="analysis-bottom-nav">
              <Button
                type="button"
                variant="secondary"
                onClick={goToPrevTab}
                disabled={!hasPrevTab}
              >
                ANTERIOR
              </Button>
              <Button
                type="button"
                variant="primary"
                onClick={goToNextTab}
                disabled={!hasNextTab}
              >
                PRÓXIMO
              </Button>
            </div>
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>

      <SolicitacaoAdequacaoModal
        show={showSolicitacaoModal}
        onClose={() => setShowSolicitacaoModal(false)}
        onOpenConfirmModal={(selecionados) => {
          setSelecionadosParaEnvio(selecionados as HistoricoAdequacaoItem[]);
          setShowSolicitacaoModal(false);
          setShowConfirmModal(true);
        }}
        itensNaoConformes={NaoConformes}
        titulosValor={titulosValor}
      />

      <AdequacaoModalConfirmacao
        show={showConfirmModal}
        onClose={() => {
          setShowConfirmModal(false);
          setShowSolicitacaoModal(true);
        }}
        onConfirm={() => {
          setShowConfirmModal(false);
          handleSubmitAdequacao(selecionadosParaEnvio);
        }}
      />
    </Container>
  );
};

export default AnalysisTab;
